import { FlexRow } from '@components/FlexRow';
import FormContainer from '@components/FormContainer';
import Icons from '@components/Icons';
import { Article } from '@components/InputRichText';
import Colors from '@constants/Colors';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { moment } from '@utils/date.utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import JobOfferProfileWantedForm from './JobOfferProfileWantedForm';


const JobOfferWantedProfile = ({ pro, jobOffer, onSubmit, loading, onEditStateChange }: { pro: any, jobOffer: any, onSubmit?: () => Promise<void>, loading?: boolean, onEditStateChange?: (editing: boolean) => void }) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const [formState, setFormState] = useState<'view' | 'edit'>('view');
    const { getValues } = useFormContext();

    const setFormStateWithCallback = (state: 'view' | 'edit') => {
        setFormState(state);
        onEditStateChange?.(state === 'edit');
    };

    const handleCancel = () => {
        setFormStateWithCallback('view');
    };

    const handleSubmit = async () => {
        await onSubmit?.();
        setFormStateWithCallback('view');
    }
    const positionExperience = getValues('position.experience');
    const minimumExperience = positionExperience > 1 ? `${positionExperience} ans` : '1 an';

    const getRequirementLanguage = () => {
        let paragraph = '';
        const listLangs = jobOffer?.candidate?.languages ?? [];
        listLangs.forEach((item, index) => {
            let level = '';
            switch (item?.levelLanguage?.score ?? 0) {
                case 1:
                    level = 'débutant';
                    break;
                case 2:
                    level = 'élémentaire';
                    break;
                case 3:
                    level = 'intermédiaire';
                    break;
                case 4:
                    level = 'avancé';
                    break;
                case 5:
                    level = 'courant';
                    break;
                default:
                    break;
            }

            if (index > 0) {
                paragraph = paragraph + ', ';
            }
            if (item?.mandatory) {
                paragraph = `${paragraph} ${item?.language?.name ?? ''} ${level} exigé`;
            } else {
                paragraph = `${paragraph} ${item?.language?.name ?? ''} ${level} est un plus`;
            }
        });
        return paragraph;
    };

    function getParagraph1() {
        const allowFreshGrad = getValues('position.allowFreshGrad');
        const hasExperiences = positionExperience?.length > 0;
        const hasTeam = getValues('position.hasTeam');
        const teamNumber = getValues('position.teamNumber');

        if (allowFreshGrad && !hasExperiences && !hasTeam)
            return `Vous êtes débutant(e) et vous souhaitez vous lancer dans une nouvelle carrière.`;

        if (!allowFreshGrad && !hasExperiences && !hasTeam)
            return `Vous êtes débutant(e) et vous souhaitez vous lancer dans une nouvelle carrière.`;

        if (allowFreshGrad && !hasExperiences && hasTeam)
            return `Vous êtes débutant(e) et vous souhaitez vous lancer dans une nouvelle carrière et avez déjà managé(e) une équipe.`;

        if (allowFreshGrad && hasExperiences && !hasTeam)
            return `Vous êtes débutant(e) ou avez au moins ${minimumExperience} d'expérience et souhaitez lancer votre carrière.`;

        if (allowFreshGrad && hasExperiences && hasTeam)
            return `Vous êtes débutant(e) avec une première approche du management ou avez au moins ${minimumExperience} d'expérience et souhaitez lancer votre carrière.`;

        if (!allowFreshGrad && hasExperiences && !hasTeam)
            return `Vous avez au moins ${minimumExperience} d'expérience et vous souhaitez lancer votre carrière.`;

        if (!allowFreshGrad && hasExperiences && hasTeam)
            return `Vous avez au moins ${minimumExperience} d'expérience et vous souhaitez lancer votre carrière. Vous aurez la responsabilité d'une équipe de ${teamNumber} personnes, votre rôle sera de les accompagner dans la réalisation de leur mission et le dépassement de leurs objectifs.`;
    }
    const getQualificationItems = (): string[] => {
        const items: string[] = [];

        // First paragraph about experience
        const paragraph1 = getParagraph1();
        if (paragraph1) {
            items.push(paragraph1);
        }

        // Specialties
        if (jobOffer?.specialties?.length > 0) {
            items.push(
                `Vous maîtrisez les techniques suivantes : ${jobOffer.specialties
                    .map(item => item.specialtyName)
                    .join(', ')}.`
            );
        }

        // Languages
        if (jobOffer?.candidate?.languages?.length > 0) {
            items.push(`Maîtrise des langues : ${getRequirementLanguage()}.`);
        }

        // Soft skills
        const softSkills = getValues('candidate.softSkills');
        if (softSkills?.length > 0) {
            items.push(
                `Ceux qui vous connaissent, vous décrivent comme quelqu'un ayant les traits de caractère suivants : ${softSkills.join(', ')}.`
            );
        }

        // Final paragraph
        const startDate = getValues('contract.startDate');
        items.push(
            `Vous vous reconnaissez dans cette description? Alors rejoignez nous et débutez une nouvelle aventure.` +
            (startDate
                ? ` Le poste est à pourvoir à partir du ${moment(startDate).format('DD MMMM yyyy')} `
                : 'Le poste est à pourvoir dès que possible')
        );

        return items;
    };

    return (
        <FormContainer
            formTitle={"Profil recherché"}
            formState={formState}
            setFormState={setFormStateWithCallback}
            loading={loading}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
        >
            {formState === 'view' ? (
                <Box display="flex" flexDirection="column" gap="16px" px="8px">
                    
                        <Stack>
                            <Stack sx={{ gap: 2 }}>
                                {!!jobOffer?.description?.qualifications && (
                                    <Box
                                        sx={{
                                            '& p': {
                                                fontSize: '14px',
                                                fontWeight: 500,
                                            },
                                        }}>
                                        <Article
                                            dangerouslySetInnerHTML={{ __html: jobOffer?.description?.qualifications }}
                                        />
                                    </Box>
                                )}
                                {getQualificationItems().map((item, index) => (
                                    <FlexRow key={index} sx={{ gap: isMobile ? 1 : 2 }}>
                                        <Icons name="check" color={Colors.PRIMARY} size={isMobile ? 20 : 24} />
                                        <Stack sx={{ flex: 1 }}>
                                            <Typography>{item}</Typography>
                                        </Stack>
                                    </FlexRow>
                                ))}
                            </Stack>

                           
                        </Stack>
                </Box>
            ) : (
                <JobOfferProfileWantedForm />
            )}
        </FormContainer>
    );
};

export default JobOfferWantedProfile;
